import { ActivePanel, GetPointResponse, GetTopicResponse, OnboardingState } from '../../types';
import { GoogleTagEvent, logWithAmplitude, logWithGoogleTag } from '../../helpers/api/api';
import { LitElement, css, html, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { getFromLocalStorage, isDefined } from '../../helpers/utils';

import { Dialog } from '@material/mwc-dialog';
import { MapComponent } from '../../components/map-component';
import { OnboardingModal } from '../../components/onboarding-modal';
import { SettingDrawer } from '../../components/mobile/setting-drawer';
import { StateController } from '../../helpers/state/state-management/state-controller';
import accountInfo from '../../helpers/state/account-info';
import challengeData from '../../helpers/state/challenge-data';
import { getQueryStringParameters } from '../../helpers/urls';
import { heights } from '../../style/variables';
import { ifDefined } from 'lit/directives/if-defined';
import pointCache from '../../helpers/state/point-cache';
import { removeInertFromVaadinOverlay } from '../../helpers/inert';
import { saveEmail } from '../../helpers/api/account-api';
import topicData from '../../helpers/state/topic-data';

@customElement('admin-portal-page-mobile')
export class AdminPortalPageMobile extends LitElement {
    amplitude_callsite: string = 'Admin Portal';

    @query('#onboardingModal')
    onboardingModal!: SettingDrawer;

    @query('#shareDrawer')
    shareMap!: SettingDrawer;

    @query('#upgradeDialogComponent')
    upgradeDialogComponent?: Dialog;

    @query('#onboardingModalComponent')
    onboardingModalComponent!: OnboardingModal;

    @query('#signinModalComponent')
    signinModalComponent!: Dialog;

    @query('#addPointDrawer')
    addPointDrawer?: SettingDrawer;

    @query('#editPointDrawer')
    editPointDrawer?: SettingDrawer;

    @query('#map-header')
    mapHeader!: HTMLElement;

    @query('#mapComponent')
    mapComponent!: MapComponent;

    @property({ type: String })
    crowdsourceLink?: string;

    @property({ type: String })
    viewonlyLink?: string;

    @state()
    focusedPoint?: GetPointResponse;

    @state()
    focusedPoints?: GetPointResponse[];

    @state()
    activePanel: ActivePanel = ActivePanel.None;

    @state()
    userSelectedLatLng?: Location; // this is used to pass into the edit point modal

    @state()
    showMapComponent: boolean = true;

    @state()
    showPointView: boolean = false; // combine into onboarding props

    @state()
    topics: GetTopicResponse[] = [];

    @state()
    showAddEmailView: boolean = false; // combine into onboarding props

    @state()
    onboardingStep?: OnboardingState = undefined;

    @state()
    onboardingHeader: string = 'Get Started';

    @state()
    showShareModal: boolean = false;

    private point_id?: string;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    @state()
    foo: boolean = false;

    @state()
    drawerOpen: boolean = false;

    async firstUpdated() {
        await this.fixVaadinFocusTrap();

        await this.startup();
    }


    private async startup() {
        const queryString = getQueryStringParameters();
        const point_id = queryString.get('point_id');

        this.point_id = point_id;

        await topicData.instantiateTopicData(
            this.amplitude_callsite,
            accountInfo.isAuthenticated,
            false,
            false
        );
        topicData.mapComponent = this.mapComponent;

        window.dispatchEvent(new CustomEvent('page-content-ready'));

        if (isDefined(topicData.topic)) {
            await pointCache.instantiatePointCache(
                topicData.topic,
                topicData.topicKey,
                false,
                true,
                challengeData.challenge ? challengeData.allCheckins : accountInfo.account_checkins,
            );

            // Ensure that local storage, amplitude, and chameleon have an email if possible
            // Because we are constantly adding new tools that require login, we should always run this to make sure all are updated
            const local_storage_email = getFromLocalStorage('email');
            const topic_email = topicData.email;
            if (accountInfo.isAuthenticated) {
                saveEmail(accountInfo.email);
            } else if (local_storage_email !== null) {
                saveEmail(local_storage_email);
            } else if (topic_email !== undefined) {
                saveEmail(topic_email);
            }

            this.conditionallyOpenOnboarding();

            // If an admin gives this link to someone without write access, then they should get redirected.
            if (!topicData.isWriter) {
                const message = `It looks like you have an edit link but we haven't logged you in yet! You can do that here.`;
                window.location.replace(
                    `/#login?callback_url=${encodeURIComponent(window.location.href)}&message=${encodeURIComponent(message)}`
                );
                return;
            }

            // Set the OpenGraph data dynamically.
            // This is not strictly required, since the redirector currently handles this for bots on the server side.
            (document as any).querySelector('meta[property="og:title"]').content = topicData.name;

            if (topicData.topic?.creator) {
                (document as any).querySelector(
                    'meta[property="og:description"]'
                ).content = `Community map made by ${topicData.topic?.creator} just for you. Check it out!`;
            }
        }
    }

    private async closeOnboardingModal(e: CustomEvent) {
        void this.onboardingModal.close();
        this.drawerOpen = false;
    }

    private async openOnboardingModal() {
        void this.onboardingModal?.open();
        this.drawerOpen = true;
    }

    private conditionallyOpenOnboarding() {
        if (topicData.topic === undefined || this.onboardingModal.isOpened) {
            return;
        }

        const inInitialView =
            (topicData.topic.default_map_center.search === 'default_map_center' ||
                topicData.topic?.name === 'New Map from Template') &&
            !pointCache.hasPoints;

        if (inInitialView) {
            this.onboardingStep = OnboardingState.Prompt;
        }

        if (this.onboardingStep === OnboardingState.Points) {
            topicData.refreshBounds();
        }

        if (inInitialView || isDefined(this.onboardingStep)) {
            void this.onboardingModal.open();
            this.drawerOpen = true;
        }
    }

    private async onShareMapClick(e: CustomEvent) {
        this.crowdsourceLink = e.detail.crowdsourceLink;
        this.viewonlyLink = e.detail.viewonlyLink;
        this.drawerOpen = true;
        this.showShareModal = true;

        logWithGoogleTag(this.amplitude_callsite, GoogleTagEvent.ADMIN_SHARE_CLICK, {
            topic_id: topicData.topicId,
            topic_name: topicData.name,
        });
    }

    private async fixVaadinFocusTrap() {
        await removeInertFromVaadinOverlay('VAADIN-CONFIRM-DIALOG-OVERLAY');
        const body = document.querySelector('body');

        if (body) {
            const observer = new MutationObserver((mutationRecords, _) => {
                const confirmDialog = mutationRecords
                    .map((m) => {
                        let element;
                        m.addedNodes.forEach((n: any) => {
                            if (n.tagName === 'VAADIN-CONFIRM-DIALOG-OVERLAY') {
                                element = n;
                            }
                        });

                        return element;
                    })
                    .filter((e) => e !== undefined)[0];

                if (confirmDialog !== undefined) {
                    (confirmDialog as any).focusTrap = false;
                }
            });

            observer.observe(body, { childList: true });
        }
    }

    render() {
        return html`
            <div class="admin-portal__page-container">
                ${topicData.isReady
                    ? html` <nav-bar
                                @on-share-map-click="${this.onShareMapClick}"
                        ></nav-bar>
                          <div class="admin-portal__section-container">
                              <map-header id="map-header"></map-header>
                              <div class="admin-portal__map-wrapper">
                                  ${this.showMapComponent
                                      ? html`<map-component
                                            id="mapComponent"
                                            .showAddPointButton=${true}
                                            .focused_point_id="${this.point_id}"
                                            isAdminPortalMobile
                                            hideEmailCapturePopUp
                                            @open-upgrade-dialog="${() => this.upgradeDialogComponent?.show()}"
                                            @open-add-point-drawer="${this.openAddPointDrawer}"
                                            @open-edit-point-drawer="${this.openEditPointDrawer}"
                                        >
                                        </map-component>`
                                      : ''}
                              </div>
                          </div>
                        <setting-controls class=${this.drawerOpen ? 'hidden' : ''} @closeUpgradeDialog=${() => this.upgradeDialogComponent?.close()}></setting-controls>
                        <setting-drawer
                            id="onboardingModal"
                            class="onboarding-drawer outsideDrawer"

                            title=${this.onboardingHeader}
                            @close="${() => {void this.onboardingModal.close(); this.drawerOpen = false;}}"
                            >
                            <onboarding-modal
                                id="onboardingModalComponent"
                                .onboardingStep=${this.onboardingStep}
                                @openSignIn="${this.signInClick}"
                                @on-close-onboarding-modal="${this.closeOnboardingModal}"
                                @on-open-onboarding-modal="${this.openOnboardingModal}"
                                @on-header-change=${(e: CustomEvent) => {
                                    this.onboardingHeader = e.detail.header;
                                }}
                            >
                            </onboarding-modal>
                        </setting-drawer>
                          <mwc-dialog id="signinModalComponent" class="dialog" hideActions>
                              <modal-dialog
                                  size="md"
                                  @close="${() => {this.signinModalComponent.close(); this.requestUpdate();} }"
                              >
                                  <signin-modal-component
                                      slot="body"
                                      ?updateEmail=${this.showAddEmailView}
                                      @close="${() => this.signinModalComponent.close()}"
                                  >
                                  </signin-modal-component>
                              </modal-dialog>
                          </mwc-dialog>`
                    : ''}
                <error-toast id="errorToast"></error-toast>
            </div>
            <setting-drawer id="editPointDrawer" class="outsideDrawer" isExpanded="${true}" @on-close=${() => {void this.editPointDrawer?.close(); this.drawerOpen = false;}}>
                <add-edit-point-component
                    .userSelectedLatLng="${this.userSelectedLatLng}"
                    bypassGoogleKey
                    .point=${this.focusedPoint}
                    @close-point-modal="${() => {
                        void this.editPointDrawer?.close();
                        this.drawerOpen = false;
                    }}"
                    @open-point-modal=${async () => await this.editPointDrawer?.open()}
                >
                </add-edit-point-component>
            </setting-drawer>
            <setting-drawer id="addPointDrawer" class="outsideDrawer" isExpanded="${true}" @on-close=${() => {void this.addPointDrawer?.close(); this.drawerOpen = false;}}>
                <add-edit-point-component
                .userSelectedLatLng="${this.userSelectedLatLng}"
                .editMode=${false}
                bypassGoogleKey
                @close-point-modal="${() => {
                    void this.addPointDrawer?.close();
                    this.drawerOpen = false;
                }}"
                @open-point-modal=${async () => await this.addPointDrawer?.open()}
                >
                </add-edit-point-component>
            </setting-drawer>
            ${this.showShareModal ? html`
                <setting-drawer id="shareDrawer" class="outsideDrawer" isExpanded="${true}" @on-close=${() => {this.drawerOpen = false; this.showShareModal = false;}} openInitially>
                    <map-sharing-component
                        crowdsourceLink="${ifDefined(this.crowdsourceLink)}"
                        viewonlyLink="${ifDefined(this.viewonlyLink)}"
                        >
                    </map-sharing-component>
                </setting-drawer>
            ` : ''}
        `;
    }

    drawerButtonClicked(e: CustomEvent) {
        const boundAction = e.detail.action.bind(this);
        boundAction(e.detail.panel);
    }

    panelButtonClicked(activePanel: ActivePanel) {
        if (this.activePanel === activePanel) {
            logWithAmplitude(this.amplitude_callsite, `Admin Drawer Close: ${this.activePanel}`, {
                topic_id: topicData.topicId,
                topic_key: topicData.topicKey,
                drawer_name: this.activePanel,
            });
            this.activePanel = ActivePanel.None;
        } else {
            this.activePanel = activePanel;
            logWithAmplitude(this.amplitude_callsite, `Admin Drawer Open: ${this.activePanel}`, {
                topic_id: topicData.topicId,
                topic_key: topicData.topicKey,
                drawer_name: this.activePanel,
            });
        }
    }

    private openAddPointDrawer(e: CustomEvent) {
        this.userSelectedLatLng = e.detail.userSelectedLatLng;
        void this.addPointDrawer?.open();
        this.drawerOpen = true;
    }

    private async openEditPointDrawer(e: CustomEvent): Promise<void> {
        this.focusedPoint = e.detail.focusedPoint;
        this.userSelectedLatLng = e.detail.userSelectedLatLng;
        void this.editPointDrawer?.open();
        this.drawerOpen = true;
    }

    private signInClick() {
        this.signinModalComponent.open = true;
    }

    static get styles() {
        return css`
            #onboardingModal {
                --bottom-height: 0px;
                --bottom-height-half: 0px;
                z-index: 11;
            }

            .admin-portal__map-wrapper {
                position: relative;
                height: 100%;
                box-shadow: rgb(0 0 0 / 19%) 0px 10px 20px, rgb(0 0 0 / 23%) 0px 6px 6px;
            }

            .admin-portal__page-container {
                height: 100%;
                display: flex;
                flex-direction: column;
            }

            .outsideDrawer {
                --bottom-height: 0px;
                --bottom-height-half: 0px;
            }

            .admin-portal__section-container {
                background-color: var(--theme-border-color);
                display: flex;
                flex-direction: column;
                margin: 20px;
                min-height: calc(
                    100% - ${unsafeCSS(heights.nav)} - ${unsafeCSS(heights.mobileSettingsControls)} -
                        40px
                );
            }

            .addPointButton {
                display: flex;
                flex-direction: column;
                align-items: center;
                background-color: var(--main-primary-color);
                color: var(--theme-white-color);
                border-style: none;
                margin: 6px;
                cursor: pointer;
                --mdc-theme-secondary: var(--main-primary-color);
                --mdc-theme-on-secondary: var(--theme-white-color);
            }

            .hidden {
                display: none;
            }
        `;
    }
}
